import { useEffect, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { PerformanceTargets } from "@shared-core/data/marketing-types";
import { extractFieldErrors } from "@shared-core/data/validation";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";

interface PerformanceTargetsDialogProps {
  open: boolean;
  targets: PerformanceTargets;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

const FIELDS: {
  key: keyof PerformanceTargets;
  label: string;
  hint: string;
}[] = [
  { key: "open_rate", label: "Opened target (%)", hint: "% of Delivered" },
  { key: "click_rate", label: "Clicked target (%)", hint: "% of Delivered" },
  {
    key: "max_unsubscribe_rate",
    label: "Unsubscribed maximum (%)",
    hint: "% of Contacts",
  },
];

function errorSummary(count: number): string {
  return `${count} target${count === 1 ? "" : "s"} need${count === 1 ? "s" : ""} attention.`;
}

// Stored as fractions, edited as percentages.
const toPercent = (fraction: number) =>
  String(Number((fraction * 100).toFixed(4)));

/** Edit the targets every send is judged against. They apply to all
 *  campaigns, past and future, as soon as they're saved. */
export function PerformanceTargetsDialog({
  open,
  targets,
  onOpenChange,
  onSaved,
}: PerformanceTargetsDialogProps) {
  const dataAdapter = useDataAdapter();
  const [values, setValues] = useState<
    Record<keyof PerformanceTargets, string>
  >({
    open_rate: toPercent(targets.open_rate),
    click_rate: toPercent(targets.click_rate),
    max_unsubscribe_rate: toPercent(targets.max_unsubscribe_rate),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof PerformanceTargets, string>>
  >({});

  useEffect(() => {
    if (open) {
      setValues({
        open_rate: toPercent(targets.open_rate),
        click_rate: toPercent(targets.click_rate),
        max_unsubscribe_rate: toPercent(targets.max_unsubscribe_rate),
      });
      setError(null);
      setFieldErrors({});
    }
  }, [open, targets]);

  async function save() {
    const parsed = Object.fromEntries(
      FIELDS.map(({ key }) => [key, Number(values[key]) / 100]),
    ) as unknown as PerformanceTargets;
    const invalid = Object.fromEntries(
      FIELDS.filter(({ key }) => {
        const v = parsed[key];
        return !Number.isFinite(v) || v <= 0 || v >= 1;
      }).map(({ key }) => [key, "Enter a percentage above 0 and below 100."]),
    );
    setFieldErrors(invalid);
    if (Object.keys(invalid).length > 0) {
      setError(errorSummary(Object.keys(invalid).length));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await dataAdapter.updatePerformanceTargets(parsed);
      onSaved();
      onOpenChange(false);
    } catch (e) {
      console.error("marketing.performance.targets_save.failed", e);
      const errors = extractFieldErrors(e);
      if (errors.length > 0) {
        setFieldErrors(
          Object.fromEntries(errors.map((f) => [f.field, f.message])),
        );
        setError(errorSummary(errors.length));
      } else {
        setError("Couldn't save the targets. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Targets</DialogTitle>
          <DialogDescription>
            Every send is judged against these. Changing them changes the ticks
            and crosses on all campaigns, past ones included.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {FIELDS.map(({ key, label, hint }) => (
            <div key={key} className="space-y-1">
              <Label htmlFor={`target-${key}`}>{label}</Label>
              <Input
                id={`target-${key}`}
                inputMode="decimal"
                value={values[key]}
                onChange={(e) =>
                  setValues({ ...values, [key]: e.target.value })
                }
                aria-invalid={fieldErrors[key] !== undefined}
                aria-describedby={
                  fieldErrors[key]
                    ? `target-${key}-hint target-${key}-error`
                    : `target-${key}-hint`
                }
              />
              <p
                id={`target-${key}-hint`}
                className="text-xs text-muted-foreground"
              >
                {hint}
              </p>
              {fieldErrors[key] && (
                <p
                  id={`target-${key}-error`}
                  className="text-sm text-red-600 dark:text-red-400"
                >
                  {fieldErrors[key]}
                </p>
              )}
            </div>
          ))}
        </div>
        {error && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={() => void save()} disabled={saving}>
            Save targets
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
